import { useQueries } from '@tanstack/react-query';
import { getAgregatedQualifications } from 'services/api/AgentCenterAPI';
import { queryKeys } from '../queryKeys';
import { useMemo } from 'react';

export const usePostBatchAggregatedQualifications = (callIds: string[]) => {
    const queries = useQueries({
        queries: callIds.map((callId) => ({
            queryKey: queryKeys.aggregatedQualifications([callId]),
            queryFn: () => getAgregatedQualifications(callId),
            enabled: callIds.length > 0,
        })),
    });

    const batchAggregatedQualificationsData = useMemo(() => {
        if (!callIds.length || queries.every((query) => !query.data)) {
            return undefined;
        }

        const combinedQualifications = callIds.reduce<Record<string, NonNullable<(typeof queries)[number]['data']>>>(
            (acc, callId, index) => {
                const qualifications = queries[index]?.data;
                if (qualifications) {
                    acc[callId] = qualifications;
                }
                return acc;
            },
            {}
        );

        return {
            qualifications: combinedQualifications,
        };
    }, [callIds, queries]);

    const isBatchAggregatedQualificationsLoading = queries.some((query) => query.isLoading);
    const hasError = queries.some((query) => query.isError);
    const error = queries.find((query) => query.isError)?.error;

    return {
        batchAggregatedQualificationsData,
        isBatchAggregatedQualificationsLoading,
        isError: hasError,
        error,
        refetch: () => {
            queries.forEach((query) => query.refetch());
        },
    };
};
